import { InboxUnreadCountClientsVariables } from '@ms/yammer-graphql';
import queryInboxUnreadCount from '@ms/yammer-graphql/dist/operations/queryInboxUnreadCount.graphql';

import {
  GraphqlApiRequest,
  ManagedApiLifecycle,
  RequestInitiator,
  getGraphqlApiRequest,
  managedApiAction,
} from '../..';
import { RetryOptions, retryableManagedApiAction } from '../../api/retryableManagedApiAction';
import { normalizeUnseenThreadEdges } from '../../normalization/graphql/normalizeUnseenThreadEdges';
import { getRetryDelay } from '../../realtime';
import {
  RealtimeChannelIdsByFeed,
  RequestStatusType,
  UnseenThreadEdgesForFeed,
  YammerState,
  inboxUnreadCountRequestId,
} from '../../state/types';
import { InfoEventName, RequestSkippedReason, reportError, reportInfo } from '../../telemetry';
import { PromiseThunkAction } from '../../types';
import { getUnreadInboxCount, maxInboxCountToShow } from '../feedCounts/selectors';
import {
  inboxAllFeedId,
  inboxUnreadFeedId,
  networkQuestionInboxAllFeedId,
  networkQuestionInboxUnreadFeedId,
} from '../feeds/getId';
import { isInboxFeedSubscribed } from '../realtimeSubscriptions/selectors';
import { getFulfilledTime, isPending } from '../requestStatuses/selectors';

import {
  InboxUnreadCountLoadFulfilledAction,
  InboxUnreadCountLoadPendingAction,
  InboxUnreadCountLoadRejectedAction,
  InboxUnreadCountResponse,
} from './actions.types';

const loadInboxUnreadCountPending = (): InboxUnreadCountLoadPendingAction => ({
  type: 'INBOX_UNREAD_COUNT.LOAD_PENDING',
});

const loadInboxUnreadCountFulfilled = (
  unseenThreadEdges: UnseenThreadEdgesForFeed[],
  realtimeChannels?: RealtimeChannelIdsByFeed
): InboxUnreadCountLoadFulfilledAction => ({
  type: 'INBOX_UNREAD_COUNT.LOAD_FULFILLED',
  payload: {
    unseenThreadEdges,
    realtimeChannels,
  },
});

const loadInboxUnreadCountRejected = (error: unknown): InboxUnreadCountLoadRejectedAction => ({
  type: 'INBOX_UNREAD_COUNT.LOAD_REJECTED',
  payload: {
    error,
  },
  error: true,
});

export interface LoadInboxUnreadCountOptions {
  readonly isRealtimeInitiated?: boolean;
}
type LoadInboxUnreadCount = (options?: LoadInboxUnreadCountOptions) => PromiseThunkAction<void>;
export const loadInboxUnreadCount: LoadInboxUnreadCount =
  (options = { isRealtimeInitiated: false }) =>
  async (dispatch, getState) => {
    const state = getState();
    if (isPending(state, RequestStatusType.InboxUnreadCountRequest, inboxUnreadCountRequestId)) {
      reportSkippedLoadUnseenCount({ reason: RequestSkippedReason.PreviousRequestPending });

      return;
    }

    const threadCount = getUnreadInboxCount(state);
    if (threadCount >= maxInboxCountToShow) {
      reportSkippedLoadUnseenCount({ threadCount, reason: RequestSkippedReason.CountAboveVisibleLimit });

      return;
    }

    const initiator = options.isRealtimeInitiated ? RequestInitiator.Realtime : undefined;
    const request: GraphqlApiRequest<InboxUnreadCountClientsVariables> = getGraphqlApiRequest({
      body: {
        operationName: 'InboxUnreadCountClients',
        query: queryInboxUnreadCount,
      },
      initiator,
    });

    const fulfilledForFeed = (
      unreadFeedId: string,
      allFeedId: string,
      threadIds: string[],
      hasMore: boolean,
      realtimeChannelId: string | null
    ) => {
      const unseenThreadEdges = [
        normalizeUnseenThreadEdges({
          feedId: unreadFeedId,
          threadIds,
          hasMore,
        }),
      ];

      const realtimeChannels: RealtimeChannelIdsByFeed = realtimeChannelId
        ? {
            [unreadFeedId]: realtimeChannelId,
            [allFeedId]: realtimeChannelId,
          }
        : {};

      return { unseenThreadEdges, realtimeChannels };
    };

    const lifecycle: ManagedApiLifecycle<InboxUnreadCountResponse> = {
      pending: () => loadInboxUnreadCountPending(),
      fulfilled: (response) => {
        let unseenThreadEdges: UnseenThreadEdgesForFeed[] = [];
        let realtimeChannels = {};

        let unreadThreadsInfo = response.data.viewer.inbox.inboxUnreadCount.unreadThreadsInfo || [];
        let threadIds = unreadThreadsInfo.map((unreadThread) => unreadThread.threadId);
        let hasMore = response.data.viewer.inbox.inboxUnreadCount.hasMore;
        let realtimeChannelId = response.data.viewer.inbox.threads.realtimeChannelId;

        const inboxFulfilled = fulfilledForFeed(
          inboxUnreadFeedId,
          inboxAllFeedId,
          threadIds,
          hasMore,
          realtimeChannelId
        );
        unseenThreadEdges = [...unseenThreadEdges, ...inboxFulfilled.unseenThreadEdges];
        realtimeChannels = { ...realtimeChannels, ...inboxFulfilled.realtimeChannels };

        unreadThreadsInfo = response.data.viewer.networkQuestionInbox.inboxUnreadCount.unreadThreadsInfo || [];
        threadIds = unreadThreadsInfo.map((unreadThread) => unreadThread.threadId);
        hasMore = response.data.viewer.networkQuestionInbox.inboxUnreadCount.hasMore;
        realtimeChannelId = response.data.viewer.networkQuestionInbox.threads.realtimeChannelId;

        const networkQuestionsFulfilled = fulfilledForFeed(
          networkQuestionInboxUnreadFeedId,
          networkQuestionInboxAllFeedId,
          threadIds,
          hasMore,
          realtimeChannelId
        );
        unseenThreadEdges = [...unseenThreadEdges, ...networkQuestionsFulfilled.unseenThreadEdges];
        realtimeChannels = { ...realtimeChannels, ...networkQuestionsFulfilled.realtimeChannels };

        return loadInboxUnreadCountFulfilled(unseenThreadEdges, realtimeChannels);
      },
      rejected: (error) => loadInboxUnreadCountRejected(error),
    };

    const requestOptions = { request, lifecycle };
    const action = options.isRealtimeInitiated
      ? retryableManagedApiAction(requestOptions, getRetryOptions(getState))
      : managedApiAction(requestOptions);

    try {
      await dispatch(action);
    } catch (error) {
      reportError({ error });
    }
  };

interface ReportSkippedLoadUnseenCountOptions {
  readonly reason: RequestSkippedReason;
  readonly threadCount?: number;
}
const reportSkippedLoadUnseenCount = (options: ReportSkippedLoadUnseenCountOptions) =>
  reportInfo({
    eventName: InfoEventName.RealtimeInboxUnreadCountRequestSkipped,
    eventProperties: {
      reason: options.reason,
      threadCount: options.threadCount,
    },
  });

const getRetryOptions = (getState: () => YammerState): RetryOptions => ({
  shouldRetry: getShouldLoadUnseenCountWithSkipTelemetry(getState),
  retryDelay: getRetryDelay(),
  retryRequestFields: { initiator: RequestInitiator.RealtimeRetry },
});

type GetShouldLoadUnseenCountWithSkipTelemetry = (getState: () => YammerState) => () => boolean;
const getShouldLoadUnseenCountWithSkipTelemetry: GetShouldLoadUnseenCountWithSkipTelemetry = (getState) => {
  const requestTime = new Date().getTime();

  return () => {
    const state = getState();

    if (isPending(state, RequestStatusType.InboxUnreadCountRequest, inboxUnreadCountRequestId)) {
      reportSkippedLoadUnseenCount({ reason: RequestSkippedReason.PreviousRequestPending });

      return false;
    }

    const fulfilledTime = getFulfilledTime(state, RequestStatusType.InboxUnreadCountRequest, inboxUnreadCountRequestId);
    const hasLoadedSinceRequest = fulfilledTime && fulfilledTime > requestTime;
    if (hasLoadedSinceRequest) {
      reportSkippedLoadUnseenCount({ reason: RequestSkippedReason.NewerRequestFulfilled });

      return false;
    }

    if (isInboxFeedSubscribed(state)) {
      reportSkippedLoadUnseenCount({ reason: RequestSkippedReason.InboxFeedSubscribed });

      return false;
    }

    return true;
  };
};
